import { useEffect, useMemo, useState } from "react";
import StaffLayout from "../StaffLayout";
import { api, formatErr } from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Check, Zap, RotateCcw, Plus, Loader2 } from "lucide-react";

// Three "milk" quick concepts — batch-save via /api/usage (deducts ingredient stock).
const MILK_SLOTS = [
  { k: "oatside", label: "Oatside", accent: "bg-amber-50 text-amber-900 border-amber-200", match: ["Oatside Full Cream Milk 1L"], defaultQty: 1, presets: [1, 2, 3] },
  { k: "milk",    label: "Milk",    accent: "bg-blue-50 text-blue-900 border-blue-200",   match: ["Fresh Milk 1L"], defaultQty: 1, presets: [1, 2, 3] },
  { k: "coconut", label: "Coconut", accent: "bg-lime-50 text-lime-900 border-lime-200",   match: ["Coconut Milk 1L"], defaultQty: 1, presets: [1, 2, 3] },
];
// Cream & ice-cream powders — same batch-save flow, different section.
const PANTRY_SLOTS = [
  { k: "whipping-cream", label: "Whipping Cream", accent: "bg-rose-50 text-rose-900 border-rose-200", match: ["Farm Fresh 100% Dairy Whipping Cream"], defaultQty: 1, presets: [1, 2, 3] },
  { k: "vanilla-ic",     label: "Vanilla IC",     accent: "bg-yellow-50 text-yellow-900 border-yellow-200", match: ["Vanilla Ice Cream Powder 1kg"], defaultQty: 1, presets: [1, 2, 3] },
  { k: "matcha-ic",      label: "Matcha IC",      accent: "bg-emerald-50 text-emerald-900 border-emerald-200", match: ["Matcha Ice Cream Powder 1kg"], defaultQty: 1, presets: [1, 2, 3] },
];
const ALL_SLOTS = [...MILK_SLOTS, ...PANTRY_SLOTS];
const MATCHA_VARIETIES = ["Byakuren 1kg", "Wakatake 1kg", "Rindo 500g", "Ayame 300g", "Aoarashi 1kg"];

function ContainerCard({ container, ingredients, onChanged, isAdmin }) {
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [refillOpen, setRefillOpen] = useState(false);

  const opts = useMemo(() => {
    const names = container.kind === "hojicha" ? ["Hojicha Type A 1kg"] : MATCHA_VARIETIES;
    return names.map((n) => ingredients.find((i) => i.name === n)).filter(Boolean);
  }, [container.kind, ingredients]);

  const currentIng = ingredients.find((i) => i.id === container.current_ingredient_id) || null;
  const stock = Number(container.stock_g || 0);
  const cap = Number(container.capacity_g || 100);
  const pct = Math.max(0, Math.min(100, (stock / cap) * 100));
  const low = stock <= 20;
  const accent = container.kind === "hojicha" ? "bg-orange-50 border-orange-200 text-orange-900" : "bg-emerald-50 border-emerald-200 text-emerald-900";

  const use = async () => {
    setErr("");
    const g = Number(qty || 0);
    if (g <= 0) { setErr("Enter grams > 0"); return; }
    if (g > stock) { setErr(`Only ${stock}g left in this tin`); return; }
    setBusy(true);
    try {
      await api.post(`/mgmt/matcha-containers/${container.id}/use`, { grams: g });
      setQty("");
      onChanged();
    } catch (e2) { setErr(formatErr(e2?.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const refill = async (ingredient_id) => {
    setErr("");
    setBusy(true);
    try {
      await api.post(`/mgmt/matcha-containers/${container.id}/refill`, { ingredient_id });
      setRefillOpen(false);
      onChanged();
    } catch (e2) { setErr(formatErr(e2?.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-sm border-2 p-4 ${accent}`} data-testid={`container-${container.id}`}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-display text-lg uppercase tracking-[0.08em]">{container.label}</h3>
        <span className={`text-xs font-mono ${low ? "text-red-700" : "opacity-70"}`} data-testid={`container-stock-${container.id}`}>
          {stock.toFixed(1)} / {cap.toFixed(0)} g
        </span>
      </div>
      {/* Progress bar */}
      <div className="h-1.5 bg-bone/60 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full transition-all ${low ? "bg-red-500" : container.kind === "hojicha" ? "bg-orange-500" : "bg-emerald-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="text-[11px] opacity-75 mb-2 truncate">
        {currentIng ? <>Filled with <span className="font-medium">{currentIng.name}</span></> : "— empty · refill to start —"}
      </p>

      <div className="flex items-center gap-2 mb-2">
        <input
          data-testid={`container-qty-${container.id}`}
          type="number"
          step="0.1"
          min="0"
          placeholder="grams used"
          className="flex-1 text-right font-mono text-lg bg-bone/70 border border-ink/10 rounded-sm px-2 py-1.5"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          disabled={stock <= 0}
        />
        <span className="text-[10px] uppercase tracking-[0.14em] opacity-70">g</span>
      </div>
      <div className="flex gap-1 mb-3">
        {[5, 10, 20, 50].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`container-preset-${container.id}-${n}`}
            onClick={() => setQty(String(n))}
            disabled={stock <= 0}
            className="flex-1 text-[10px] uppercase tracking-[0.14em] py-1 rounded-sm bg-bone/40 hover:bg-bone/70 border border-ink/10 disabled:opacity-40"
          >
            +{n}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          data-testid={`container-use-${container.id}`}
          onClick={use}
          disabled={busy || !Number(qty) || stock <= 0}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full px-3 py-1.5 bg-ink text-bone text-[10px] uppercase tracking-[0.18em] hover:bg-matcha-dark transition-colors disabled:opacity-40"
        >
          {busy ? "…" : "Log use"}
        </button>
        {isAdmin && (
          <button
            data-testid={`container-refill-${container.id}`}
            onClick={() => setRefillOpen((v) => !v)}
            disabled={busy || stock >= cap}
            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 bg-bone/60 border border-ink/15 text-[10px] uppercase tracking-[0.18em] hover:border-ink/40 transition-colors disabled:opacity-40"
            title={stock >= cap ? "Already full" : "Refill from bulk"}
          >
            <RotateCcw size={11} /> Refill
          </button>
        )}
      </div>

      {refillOpen && (
        <div className="mt-3 p-2 bg-bone/70 rounded-sm border border-ink/10" data-testid={`container-refill-menu-${container.id}`}>
          <p className="overline text-[9px] mb-1.5">Pick bulk to draw from</p>
          <div className="grid grid-cols-1 gap-1">
            {opts.map((o) => (
              <button
                key={o.id}
                data-testid={`container-refill-opt-${container.id}-${o.id}`}
                onClick={() => refill(o.id)}
                disabled={busy || Number(o.stock_qty || 0) <= 0}
                className="text-left text-[11px] px-2 py-1 rounded-sm hover:bg-bone flex justify-between items-center disabled:opacity-40"
              >
                <span>{o.name}</span>
                <span className="font-mono opacity-70">{o.stock_qty || 0} g bulk</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {err && <p className="text-[11px] text-red-700 mt-2" data-testid={`container-err-${container.id}`}>{err}</p>}
    </div>
  );
}

function renderSlotTile(s, selected, ingredients, setSlot) {
  const sel = selected[s.k] || {};
  const activeIng = ingredients.find((i) => i.id === sel.ingredient_id);
  const currentStock = activeIng ? Number(activeIng.stock_qty || 0) : null;
  const unit = activeIng?.unit || "";
  const disabled = s.options.length === 0;
  return (
    <div key={s.k} data-testid={`qu-tile-${s.k}`} className={`rounded-sm border-2 p-4 transition-all ${s.accent} ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-display text-2xl uppercase tracking-[0.08em]">{s.label}</h3>
        {currentStock !== null && <span className="text-[10px] uppercase tracking-[0.14em] opacity-70">stock {currentStock} {unit}</span>}
      </div>
      {s.options[0] && (
        <div className="flex items-center gap-2 mb-2">
          {s.options[0].image && <img src={s.options[0].image} alt="" className="w-8 h-8 rounded-full object-cover border border-ink/10" data-testid={`qu-thumb-${s.k}`} />}
          <p className="text-[11px] opacity-75 truncate flex-1">{s.options[0].name}</p>
        </div>
      )}
      <div className="flex items-center gap-2 mt-1">
        <input data-testid={`qu-qty-${s.k}`} type="number" step="0.001" min="0" placeholder={String(s.defaultQty)} value={sel.qty ?? ""} onChange={(e) => setSlot(s.k, { qty: e.target.value })} className="flex-1 text-right font-mono text-lg bg-bone/70 border border-ink/10 rounded-sm px-2 py-1.5" />
        <span className="text-[10px] uppercase tracking-[0.14em] opacity-70 w-8 text-left">{unit || "—"}</span>
      </div>
      <div className="flex gap-1 mt-2">
        {s.presets.map((n) => (
          <button key={n} type="button" data-testid={`qu-preset-${s.k}-${n}`} onClick={() => setSlot(s.k, { qty: n, ingredient_id: sel.ingredient_id || s.options[0]?.id })} className="flex-1 text-[10px] uppercase tracking-[0.14em] py-1 rounded-sm bg-bone/40 hover:bg-bone/70 border border-ink/10">+{n}</button>
        ))}
      </div>
    </div>
  );
}

export default function QuickUsage() {
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [containers, setContainers] = useState([]);
  const [selected, setSelected] = useState({}); // milk-slot selections
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [recent, setRecent] = useState([]);
  // "+100g" quick-powder tiles — matcha grades + hojicha + coffee.
  const [powderGrades, setPowderGrades] = useState({ matcha: [], hojicha: [], coffee: [] });
  const [powderPending, setPowderPending] = useState({});
  // Count of today's +100g taps per variety_id (so the tile shows a 1,2,3 badge).
  const [tapCounts, setTapCounts] = useState({});
  const [todayTotals, setTodayTotals] = useState(null);

  const loadPowderGrades = async () => {
    try {
      const { data } = await api.get("/mgmt/powder-grades");
      setPowderGrades({
        matcha:  data.matcha  || [],
        hojicha: data.hojicha || [],
        coffee:  data.coffee  || [],
      });
    } catch { /* silent */ }
  };

  const loadTapCounts = async () => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      const { data } = await api.get(`/mgmt/daily-powder/${today}`);
      const counts = {};
      (data?.bottles || []).forEach((b) => {
        if (b.label === "Quick +100g" && b.variety_id) {
          counts[b.variety_id] = (counts[b.variety_id] || 0) + 1;
        }
      });
      setTapCounts(counts);
      setTodayTotals(data?.totals || null);
    } catch { setTapCounts({}); setTodayTotals(null); }
  };

  const tapPowder = async (g) => {
    if (powderPending[g.id]) return;
    setPowderPending((p) => ({ ...p, [g.id]: true }));
    // Optimistic bump of the badge count for instant feedback.
    setTapCounts((c) => ({ ...c, [g.id]: (c[g.id] || 0) + 1 }));
    try {
      const { data } = await api.post("/mgmt/quick-usage", { variety_id: g.id });
      toast.success(`+100g ${g.name}`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/mgmt/quick-usage/${data.bottle_key}`);
              toast(`Refunded 100g to ${g.name}`);
              loadPowderGrades(); loadIngredients(); loadTapCounts();
            } catch { toast.error("Undo failed"); }
          },
        },
      });
      loadPowderGrades(); loadIngredients(); loadTapCounts();
    } catch (e) {
      // Rollback the optimistic bump on failure.
      setTapCounts((c) => ({ ...c, [g.id]: Math.max(0, (c[g.id] || 1) - 1) }));
      toast.error(e?.response?.data?.detail || "Could not log");
    } finally {
      setPowderPending((p) => { const n = { ...p }; delete n[g.id]; return n; });
    }
  };

  const loadIngredients = async () => {
    const { data } = await api.get("/mgmt/ingredients").catch(() => api.get("/ingredients/public"));
    setIngredients(data);
  };
  const loadContainers = async () => {
    const { data } = await api.get("/mgmt/matcha-containers");
    setContainers(data);
  };
  const loadRecent = async (d) => {
    try {
      const { data } = await api.get(`/usage?on_date=${d}`);
      setRecent(data);
    } catch (_e) { /* silently ignore */ }
  };

  useEffect(() => { loadIngredients(); loadContainers(); loadPowderGrades(); loadTapCounts(); }, []);
  useEffect(() => { loadRecent(date); }, [date]);

  const slotResolutions = useMemo(() => {
    const by_name = Object.fromEntries(ingredients.map((i) => [i.name, i]));
    const resolve = (s) => ({ ...s, options: s.match.map((n) => by_name[n]).filter(Boolean) });
    return { milk: MILK_SLOTS.map(resolve), pantry: PANTRY_SLOTS.map(resolve) };
  }, [ingredients]);

  useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev };
      [...slotResolutions.milk, ...slotResolutions.pantry].forEach((s) => {
        if (s.options.length === 1 && !next[s.k]) {
          next[s.k] = { ingredient_id: s.options[0].id, qty: "" };
        }
      });
      return next;
    });
  }, [slotResolutions]);

  const setSlot = (k, patch) => setSelected((p) => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));

  const activeLines = useMemo(
    () => ALL_SLOTS
      .map((s) => selected[s.k])
      .filter((x) => x && x.ingredient_id && Number(x.qty) > 0)
      .map((x) => ({ ingredient_id: x.ingredient_id, qty: Number(x.qty) })),
    [selected]
  );

  const saveMilks = async () => {
    setErr(""); setOk("");
    if (activeLines.length === 0) { setErr("Enter at least one quantity."); return; }
    setBusy(true);
    try {
      await api.post("/usage", { date, items: activeLines });
      setOk(`Logged ${activeLines.length} item${activeLines.length > 1 ? "s" : ""}`);
      setSelected({});
      loadIngredients();
      loadRecent(date);
      setTimeout(() => setOk(""), 2500);
    } catch (e2) { setErr(formatErr(e2?.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const userName = user?.preferred_name || user?.full_name || user?.email || "you";
  const isAdmin = user?.role === "admin";

  return (
    <StaffLayout>
      <div className="max-w-5xl mx-auto" data-testid="quick-usage-page">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="overline text-matcha-dark mb-1 flex items-center gap-1.5"><Zap size={14} /> Quick Usage</p>
            <h1 className="font-display text-3xl sm:text-4xl text-ink" style={{ letterSpacing: "0.05em" }}>Today&apos;s daily consumption</h1>
            <p className="text-sm text-ink-soft mt-1">Milks batch-save. Tins deduct instantly.</p>
          </div>
          <div className="text-right">
            <p className="overline text-ink-soft text-[10px]">Recording as</p>
            <p className="font-display text-lg text-ink" data-testid="qu-attribution">{userName}</p>
            <input data-testid="qu-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 text-xs bg-transparent border-b border-ink/15 focus:border-matcha-dark focus:outline-none px-1 py-0.5" />
          </div>
        </div>

        {/* Milk tiles */}
        <p className="overline text-ink-soft mb-3">Milks · pack</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {slotResolutions.milk.map((s) => renderSlotTile(s, selected, ingredients, setSlot))}
        </div>

        {/* Cream & ice-cream tiles */}
        <p className="overline text-ink-soft mb-3">Cream & Ice Cream · pack</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          {slotResolutions.pantry.map((s) => renderSlotTile(s, selected, ingredients, setSlot))}
        </div>

        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-3" data-testid="qu-err">{err}</p>}
        {ok && <p className="text-sm text-matcha-dark bg-sage/40 px-3 py-2 rounded-sm mb-3 flex items-center gap-1.5" data-testid="qu-ok"><Check size={14} /> {ok}</p>}

        <div className="flex items-center justify-between gap-3 flex-wrap mb-8">
          <p className="text-sm text-ink-soft" data-testid="qu-summary">{activeLines.length === 0 ? "Nothing queued" : `${activeLines.length} item${activeLines.length > 1 ? "s" : ""} ready`}</p>
          <button data-testid="qu-save" onClick={saveMilks} disabled={busy || activeLines.length === 0} className="inline-flex items-center gap-2 rounded-full px-8 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-40">{busy ? "Saving…" : "Log usage"}</button>
        </div>

        {/* Container tiles REMOVED — replaced by one-tap +100g tiles below */}

        {/* +100g Quick Powder tiles — matcha grades, hojicha, coffee */}
        <div className="mt-10 pt-6 border-t border-ink/10">
          <p className="overline text-ink-soft mb-1 flex items-center gap-1.5"><Plus size={12} /> One-tap +100g · auto deducts stock</p>
          <p className="text-xs text-ink-soft/80 mb-3">Tap a grade to log a fresh 100g top-up for today. Undo in the toast if you tapped by mistake.</p>

          {/* Today's running total — proves taps are reflected on the monthly chart */}
          <div className="bg-ink text-bone rounded-sm p-3 md:p-4 mb-5 flex items-center justify-between gap-3 flex-wrap" data-testid="qu-today-totals">
            <div>
              <p className="overline text-bone/60 text-[10px]">Today's usage (reflects on monthly chart)</p>
              <p className="font-display text-xl md:text-2xl mt-0.5 tabular-nums">
                {((todayTotals?.matcha_g || 0) + (todayTotals?.hojicha_g || 0) + (todayTotals?.coffee_g || 0)).toFixed(0)}
                <span className="text-sm text-bone/60 ml-1">g total</span>
              </p>
            </div>
            <div className="flex gap-4 md:gap-6 text-right">
              <div>
                <p className="overline text-bone/60 text-[9px]">Matcha</p>
                <p className="font-display text-lg mt-0.5 text-matcha-dark" style={{ color: "#9ab28a" }}>{(todayTotals?.matcha_g || 0).toFixed(0)}<span className="text-xs text-bone/60 ml-0.5">g</span></p>
              </div>
              <div>
                <p className="overline text-bone/60 text-[9px]">Hojicha</p>
                <p className="font-display text-lg mt-0.5 text-amber-300">{(todayTotals?.hojicha_g || 0).toFixed(0)}<span className="text-xs text-bone/60 ml-0.5">g</span></p>
              </div>
              <div>
                <p className="overline text-bone/60 text-[9px]">Coffee</p>
                <p className="font-display text-lg mt-0.5 text-stone-300">{(todayTotals?.coffee_g || 0).toFixed(0)}<span className="text-xs text-bone/60 ml-0.5">g</span></p>
              </div>
            </div>
          </div>
          {["matcha", "hojicha", "coffee"].map((kind) => {
            const list = powderGrades[kind] || [];
            if (list.length === 0) return null;
            const accent = kind === "matcha" ? "text-matcha-dark"
                         : kind === "hojicha" ? "text-amber-800"
                         : "text-stone-700";
            const bg = kind === "matcha" ? "bg-sage/15"
                     : kind === "hojicha" ? "bg-amber-50/40"
                     : "bg-stone-50";
            const label = kind === "matcha" ? "Matcha grades"
                        : kind === "hojicha" ? "Hojicha"
                        : "Coffee";
            return (
              <div key={kind} className="mb-6" data-testid={`qu-powder-${kind}`}>
                <h3 className={`overline text-[11px] mb-2 ${accent}`}>{label}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {list.map((g) => {
                    const stock = Number(g.stock_qty || 0);
                    const low = stock <= 0.5;
                    const stockLabel = g.stock_qty != null ? `${stock.toFixed(1)} ${g.unit || ""}`.trim() : "—";
                    const count = tapCounts[g.id] || 0;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => tapPowder(g)}
                        disabled={!!powderPending[g.id]}
                        data-testid={`qu-powder-tile-${g.id}`}
                        className={`group relative text-left rounded-sm border transition-all duration-200 active:scale-[0.97]
                          ${low ? "border-red-400/60 bg-red-50/40" : `border-ink/10 ${bg}`}
                          hover:border-ink/40 hover:shadow-md disabled:opacity-60 p-3 min-h-[100px]`}
                      >
                        {count > 0 && (
                          <span
                            data-testid={`qu-powder-count-${g.id}`}
                            className={`absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full
                              bg-ink text-bone text-[11px] font-display tabular-nums
                              flex items-center justify-center shadow-md`}
                            title={`${count} × +100g logged today`}
                          >
                            {count}
                          </span>
                        )}
                        <div className="flex items-start justify-between">
                          <p className="overline text-ink-soft text-[9px]">{kind}</p>
                          {powderPending[g.id]
                            ? <Loader2 size={14} className="animate-spin text-ink-soft" />
                            : <Plus size={14} className={`${accent} opacity-60 group-hover:opacity-100`} />}
                        </div>
                        <p className="font-display text-base md:text-lg text-ink mt-1 leading-tight">{g.name}</p>
                        <p className={`text-[11px] mt-1.5 tabular-nums ${low ? "text-red-700" : "text-ink-soft"}`}>
                          Stock {stockLabel}
                        </p>
                        <p className="text-[10px] uppercase tracking-[0.14em] text-ink mt-1.5">
                          {count > 0 ? `+${count * 100}g today · tap for more` : "+100g · tap"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Today's log */}
        {recent.length > 0 && (
          <div className="mt-10 pt-6 border-t border-ink/10">
            <p className="overline text-ink-soft mb-3">Today&apos;s usage log</p>
            <ul className="space-y-1.5" data-testid="qu-recent">
              {recent.slice(0, 20).map((r) => (
                <li key={r.id} className="flex items-center justify-between text-xs">
                  <span className="text-ink-soft">{(r.created_at || "").slice(11, 16)} · {r.logged_by_name} · {r.ingredient_name}{r.container_label ? ` · ${r.container_label}` : ""}</span>
                  <span className="font-mono text-red-700">−{r.qty} {r.unit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </StaffLayout>
  );
}
